import { MediaReissueResponse } from './mediaReissueResponse';
import { removePdfHeader, updateGuestMedia } from './module';
import * as R from 'ramda';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../../../core/redux/api/circuitBreaker/module';
import { IAppState } from '../../../../core/redux/IAppState';
import { API_MEDIA_REISSUE } from '../../../../core/redux/api/endpoints';
import { updateGuest } from '../../../module';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from '../../../../core/redux/api/reduxStandardApiAction';
import { isNilOrEmpty } from '../../../../language/module';
import { ACTION_TYPE } from '../../../../core/redux/actionType';
import { getResource } from '../../../../language/resources/getResource';


export const KEY_ACTION = ACTION_TYPE.REISSUE_MEDIA_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.REISSUE_MEDIA_REQUEST,
  ACTION_TYPE.REISSUE_MEDIA_RESPONSE,
  ACTION_TYPE.REISSUE_MEDIA_ERROR,
];


export const reissueMediaAction = (guests: Array<{ guestId: string, entitlementTypeId: string | undefined }>) => makeApiAction(
  {
    method:   'POST',
    endpoint: API_MEDIA_REISSUE,
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body: state =>    JSON.stringify(
      guests.map(guest => ({
        'userId': state.userState.userId,
        'masterGuestId': guest.guestId,
        'entitlementTypeId':       guest.entitlementTypeId || '',
        'reissueSource': 'ResortTools',
        'resortAbbrv': state.selectedResort.resortAbbreviation,
        "operatorId": state.userState.userName
      })),
    ),
    types:    ACTIONS.map(action => ({ type: action, meta: { guests } })) as RsaaTypes,
    bailout:  state => hasTrippedBreaker(state, KEY_ACTION)
                       || R.any(guest => !!getLoading(state, guest.guestId, KEY_ACTION), guests),
  },
);


const reducer: ApiReducer<MediaReissueResponse> = (state, action, error, isLoading, payload) => {
  const guests = action.meta.guests as Array<{ guestId: string, mediaId: string | undefined }>;

  if (isLoading) {
    return guests.reduce((running, next) => addLoading(running, next.guestId, KEY_ACTION), state);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error || isNilOrEmpty(payload) || isNilOrEmpty(payload!.pdfBase64)) {
    return guests.reduce(
      (running, next) => addError(
        running,
        next.guestId,
        KEY_ACTION,
        error || getResource(state.locale, 'REISSUE_MEDIA_ACTION_UNKNOWN_ERROR'),
        { guest: state.guests.get(next.guestId) },
      ),
      withBreaker,
    );
  }

  const noLoadingOrError = guests.reduce(
    (running, next) => removeLoadingAndError(running, next.guestId, KEY_ACTION),
    withBreaker,
  );

  const pdf = removePdfHeader(payload!.pdfBase64);

  const withPdf = R.assocPath(
    [ 'passPrinting', 'pdfs' ],
    noLoadingOrError.passPrinting.pdfs.concat({ guestIds: guests.map(guest => guest.guestId), pdf: pdf }),
    noLoadingOrError,
  );

  return payload!.guests.reduce(
    (running, next) => updateGuest(running, next.masterGuestId, guest => updateGuestMedia(
      guest,
      next.mediaID,
      new Date(),
    )),
    withPdf,
  );
};


export const reissueMediaReducer = makeApiReducer<MediaReissueResponse>(ACTIONS, KEY_ACTION, reducer);
